import { FlexColumn } from '@editor/components/utilities/layouts';
import { P4 } from '@repo/ui/components/Typography';
import {
  darkBackgroundBlur,
  handleTextOverflow,
} from '@repo/ui/components/utilities';
import { themeStyle } from '@repo/ui/theming';
import { layeringCSSVariables } from '@repo/ui-kit/css/utils';
import styled from 'styled-components';

import { OptionCustomStyles, CustomStyles } from './types';

interface DropdownOptionWrapperProps {
  compact: boolean;
  small: boolean;
  isActive: boolean;
  isCustomRender: boolean;
  customStyles: OptionCustomStyles;
  selectable: boolean;
  maxHeight?: number;
  sizeString?: string;
}

interface DropdownWrapperProps {
  small: boolean;
  rightAligned: boolean;
  compact: boolean;
  isOnTop: boolean;
  customStyles?: CustomStyles;
  maxHeight: number;
}

export const DropdownWrapper = styled(FlexColumn)<DropdownWrapperProps>`
  box-sizing: border-box;
  position: absolute;
  z-index: ${layeringCSSVariables.zIndexOverlay};
  min-width: 100%;
  width: ${({ customStyles }) => customStyles?.width ?? 'fit-content'};
  max-width: ${({ customStyles }) => customStyles?.maxWidth ?? 'unset'};
  max-height: ${({ maxHeight }) => `${maxHeight}px`};
  padding: ${({ compact }) => (compact ? '4px' : '8px')};
  row-gap: 2px;
  background: ${themeStyle.varBackgroundAlt};
  border-radius: ${themeStyle.radiusMedium};
  box-shadow: ${({ customStyles }) =>
    customStyles?.boxShadow ?? themeStyle.varShadowMedium};
  border: ${({ customStyles }) => customStyles?.border ?? ''};
  overflow-y: auto;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 5px;
  }
  font-weight: ${themeStyle.fontSemiBold};
  font-size: ${({ small }) =>
    small ? themeStyle.fontSize12 : themeStyle.fontSize14};

  top: ${({ isOnTop }) => (isOnTop ? '0' : '100%')};

  ${({ rightAligned, customStyles }) => {
    // if customStyles.right or customStyles.left is provided, use it over rightAligned
    if (customStyles?.right || customStyles?.left) {
      return `
        ${customStyles.right ? `right: ${customStyles.right}` : ''}
        ${customStyles.left ? `left: ${customStyles.left}` : ''}
      `;
    }

    return rightAligned ? 'right: 0' : 'left: 0';
  }};

  transform: ${({ isOnTop }) => isOnTop && 'translate(0, -100%)'};

  visibility: hidden;

  margin-top: 0;
  opacity: 0;

  ${darkBackgroundBlur}

  &.enter-active {
    margin-top: ${({ isOnTop }) => (isOnTop ? '-8px' : '8px')};
    opacity: 1;
    visibility: visible;
    transition-property: opacity, margin-top;
    transition-duration: ${themeStyle.transitionDuration};
  }

  &.enter-done {
    margin-top: ${({ isOnTop }) => (isOnTop ? '-8px' : '8px')};
    opacity: 1;
    visibility: visible;
  }

  &.exit {
    margin-top: ${({ isOnTop }) => (isOnTop ? '-8px' : '8px')};
    opacity: 1;
    visibility: visible;
  }

  &.exit-active {
    margin-top: 0;
    opacity: 0;
    transition-property: opacity, margin-top;
    transition-duration: ${themeStyle.transitionDuration};
  }

  &.exit-done {
    visibility: hidden;
  }
`;

const getHeightOrLineHeight = ({
  small,
  compact,
}: Pick<DropdownOptionWrapperProps, 'small' | 'compact'>): string =>
  compact ? (small ? '20px' : '24px') : small ? '31px' : '32px';

export const DropdownOptionWrapper = styled.div<DropdownOptionWrapperProps>`
  ${({ isCustomRender }) =>
    !isCustomRender
      ? `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  `
      : ''}

  box-sizing: border-box;
  flex-shrink: 0;
  height: ${(props) =>
    props.customStyles.height ?? getHeightOrLineHeight(props)};
  line-height: ${(props) =>
    props.customStyles.lineHeight ?? getHeightOrLineHeight(props)};
  padding: ${({ small, compact, customStyles }) =>
    customStyles.padding ??
    (compact ? (small ? '0 7px' : '0 10px') : small ? '0 8px' : '0 12px')};
  border-radius: ${themeStyle.radiusMedium};
  color: ${themeStyle.varInkMedium};
  cursor: ${({ selectable }) => (selectable ? 'pointer' : 'default')};
  ${handleTextOverflow};

  ${({ selectable, sizeString }) => {
    if (selectable) {
      return `&:hover {
                color: ${themeStyle.varInkMain};
                background: ${themeStyle.varInkSuperLight};

                ${sizeStringStyles(sizeString)}
              }
            `;
    }

    return `color: ${themeStyle.varInkLightMedium}`;
  }}
`;

const sizeStringStyles = (sizeString: string | undefined) => {
  if (sizeString) {
    return `.title {
      color: transparent;
    }
    .title:before {
      content: '${sizeString}';
      width: 78%;
      position: absolute;
      border-radius: ${themeStyle.radiusMedium};
      background: transparent;
      font-size: ${themeStyle.fontSize12};
      font-weight: ${themeStyle.fontSemiBold};
      color: ${themeStyle.varInkMain};
    }`;
  }
  return '';
};

export const GroupTitle = styled(P4)`
  padding-left: 8px;
  cursor: default;
`;
